import React, { useState, useEffect } from 'react';
import { GripVertical, Eye, EyeOff, Save, RotateCcw, FileText, CheckCircle, Eye as ViewIcon, Download, Trash2, SlidersHorizontal } from 'lucide-react';
import formService, { FormField } from '../../../services/form.service';
import documentListConfigService, { ColumnConfig as ServiceColumnConfig, DocumentListConfigRequest } from '../../../services/documentListConfig.service';
import Toast, { ToastType } from '../../Common/Toast';
import Loading from '../../Common/Loading';
import styles from './DocumentListingView.module.css';

interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  order: number;
  width: number;
  type: string;
  required: boolean;
  isSystem: boolean;
  fieldType?: string;
  formName?: string;
}

const DocumentListingView: React.FC = () => {
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const tablePreviewRef = React.useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const defaultSystemColumns: ColumnConfig[] = [
    { id: 'name', label: 'Document Name', visible: true, order: 1, width: 200, type: 'text', required: false, isSystem: true },
    { id: 'type', label: 'Type', visible: true, order: 2, width: 120, type: 'text', required: false, isSystem: true },
    { id: 'size', label: 'Size', visible: true, order: 3, width: 100, type: 'text', required: false, isSystem: true },
    { id: 'pages', label: 'Pages', visible: true, order: 4, width: 80, type: 'number', required: false, isSystem: true },
    { id: 'uploadedAt', label: 'Uploaded', visible: true, order: 5, width: 150, type: 'date', required: false, isSystem: true },
    { id: 'status', label: 'Status', visible: true, order: 6, width: 120, type: 'text', required: true, isSystem: true },
    { id: 'actions', label: 'Actions', visible: true, order: 7, width: 100, type: 'text', required: true, isSystem: true }
  ];

  useEffect(() => {
    loadColumns();
  }, []);

  const checkScroll = () => {
    if (tablePreviewRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tablePreviewRef.current;
      const hasOverflow = scrollWidth > clientWidth;
      const atTheEnd = scrollLeft + clientWidth >= scrollWidth - 5;
      setIsScrolled(hasOverflow && !atTheEnd);
    }
  };

  useEffect(() => {
    const container = tablePreviewRef.current;
    if (!container) return;

    // Wait for paint so scrollWidth is accurate
    const frameId = requestAnimationFrame(checkScroll);

    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    // ResizeObserver catches table width changes (columns toggled)
    const ro = new ResizeObserver(checkScroll);
    ro.observe(container);

    return () => {
      cancelAnimationFrame(frameId);
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      ro.disconnect();
    };
  }, [columns]);

  const loadColumns = async () => {
    try {
      setLoading(true);

      let allColumns = [...defaultSystemColumns];
      let formLoaded = false;

      try {
        const res = await formService.getActiveForm();
        if (res?.has_active_form && res.form) {
          const form = res.form;
          formLoaded = true;
          if (form.fields?.length) {
            const formColumns: ColumnConfig[] = form.fields.map(
              (field: FormField, index: number) => ({
                id: `form_${field.id}`,
                label: field.label,
                visible: false,
                order: defaultSystemColumns.length + index + 1,
                width: 150,
                type: field.field_type || 'text',
                required: false,
                isSystem: false,
                fieldType: field.field_type,
                formName: form.name
              })
            );
            allColumns = [...allColumns, ...formColumns];
          }
        }
      } catch (error) {
        console.log('No active form found or error loading form fields');
      }

      try {
        const response = await documentListConfigService.getMyConfig();
        if (response.configuration && response.configuration.columns) {
          const savedColumns = response.configuration.columns;
          const availableColumnsMap = new Map(allColumns.map(col => [col.id, col]));

          const mergedColumns: ColumnConfig[] = savedColumns
            .filter(savedCol => {
              if (formLoaded && savedCol.id.startsWith('form_')) {
                return availableColumnsMap.has(savedCol.id);
              }
              return true;
            })
            .map(savedCol => {
              const existingCol = availableColumnsMap.get(savedCol.id);
              if (existingCol) {
                return {
                  ...savedCol,
                  isSystem: existingCol.isSystem,
                  formName: existingCol.formName,
                  fieldType: existingCol.fieldType
                };
              }
              return { ...savedCol, isSystem: true };
            });

          allColumns.forEach(col => {
            if (!savedColumns.find(s => s.id === col.id)) {
              mergedColumns.push(col);
            }
          });

          allColumns = mergedColumns;
        }
      } catch (error) {
        console.error('Error fetching saved column config:', error);
      }

      const fixedIds = ['status', 'actions'];
      const normalColumns = allColumns.filter(col => !fixedIds.includes(col.id));
      const fixedColumns = allColumns.filter(col => fixedIds.includes(col.id));
      fixedColumns.sort((a) => (a.id === 'status' ? -1 : 1));

      allColumns = [...normalColumns, ...fixedColumns].map((col, index) => ({
        ...col,
        order: index + 1
      }));

      setColumns(allColumns);
    } catch (error) {
      console.error('Error loading columns:', error);
      setToast({ message: 'Failed to load column configuration', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    if (columnId === 'status' || columnId === 'actions') { e.preventDefault(); return; }
    setDraggedItem(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) { setDraggedItem(null); return; }
    if (targetId === 'status' || targetId === 'actions') { setDraggedItem(null); return; }

    const draggedIndex = columns.findIndex(col => col.id === draggedItem);
    const targetIndex = columns.findIndex(col => col.id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) { setDraggedItem(null); return; }

    const newColumns = [...columns];
    const [draggedColumn] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, draggedColumn);

    const fixedIds = ['status', 'actions'];
    const normalCols = newColumns.filter(col => !fixedIds.includes(col.id));
    const fixedCols = newColumns.filter(col => fixedIds.includes(col.id));
    setColumns([...normalCols, ...fixedCols].map((col, index) => ({ ...col, order: index + 1 })));
    setDraggedItem(null);
  };

  const toggleVisibility = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.visible && !column.required) {
      setColumns(prev => prev.map(col => col.id === columnId ? { ...col, visible: false } : col));
    } else if (!column?.visible) {
      setColumns(prev => prev.map(col => col.id === columnId ? { ...col, visible: true } : col));
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      const config: DocumentListConfigRequest = {
        columns: columns.map(col => ({
          id: col.id,
          label: col.label,
          visible: col.visible,
          order: col.order,
          width: col.width,
          type: col.type,
          required: col.required,
          isSystem: col.isSystem,
          formName: col.formName
        })),
        viewportWidth: window.innerWidth
      };
      await documentListConfigService.saveUserConfig(config);
      setToast({ message: 'Column configuration saved successfully', type: 'success' });
    } catch (error) {
      console.error('Error saving configuration:', error);
      setToast({ message: 'Failed to save configuration', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    setColumns([...defaultSystemColumns]);
    try {
      await documentListConfigService.deleteUserConfig();
      setToast({ message: 'Configuration reset to default', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to reset configuration', type: 'error' });
    }
  };

  if (loading) return <Loading message="Loading column configuration..." />;

  const visibleCount = columns.filter(col => col.visible).length;

  const renderColumnItem = (column: ColumnConfig) => {
    const isFixed = column.id === 'status' || column.id === 'actions';
    return (
      <div
        key={column.id}
        className={`${styles.columnItem} ${column.visible ? '' : styles.colHidden} ${draggedItem === column.id ? styles.dragging : ''} ${isFixed ? styles.fixedItem : ''}`}
        draggable={!isFixed}
        onDragStart={(e) => handleDragStart(e, column.id)}
        onDragOver={isFixed ? undefined : handleDragOver}
        onDrop={(e) => handleDrop(e, column.id)}
      >
        <div
          className={styles.dragHandle}
          style={{ opacity: isFixed ? 0.2 : 1, pointerEvents: isFixed ? 'none' : 'auto' }}
        >
          <GripVertical size={15} />
        </div>

        <div className={styles.columnInfo}>
          <span className={styles.columnLabel}>{column.label}</span>
          {isFixed && <span className={styles.fixedBadge}>Fixed</span>}
          {!column.isSystem && (
            <span className={styles.formBadge} title={column.formName || 'Form field'}>
              {column.formName || 'Form'}
            </span>
          )}
        </div>

        <div className={styles.visibilityToggle}>
          {column.required ? (
            <span className={styles.requiredLabel}>Required</span>
          ) : (
            <button
              className={`${styles.toggleButton} ${column.visible ? styles.visible : styles.hidden}`}
              onClick={() => toggleVisibility(column.id)}
              title={column.visible ? 'Hide column' : 'Show column'}
            >
              {column.visible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>

      {/* ── Scrollable area: header + column list ── */}
      <div className={styles.scrollArea}>

        {/* Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <SlidersHorizontal size={18} />
            </div>
            <div>
              <h2 className={styles.title}>Document Listing View</h2>
              <p className={styles.description}>Drag to reorder · toggle eye to show/hide · save to apply</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.resetButton} onClick={resetToDefault}>
              <RotateCcw size={13} />
              Reset
            </button>
            <button className={styles.saveButton} onClick={saveConfiguration} disabled={saving}>
              <Save size={13} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* All columns merged in one list */}
        <div className={styles.columnList}>
          {columns.map(renderColumnItem)}
        </div>

      </div>

      {/* ── Fixed preview bar at bottom ── */}
      <div className={styles.previewBar}>
        <div className={styles.previewBarHeader}>
          <span className={styles.previewTitle}>
            <FileText size={12} />
            Live Preview
          </span>
          <span className={styles.visibleCount}>{visibleCount} visible</span>
        </div>
        <div
          ref={tablePreviewRef}
          className={`${styles.previewTableWrap} ${isScrolled ? styles.hasScrollShadow : ''}`}
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: '36px' }}>
                  <input type="checkbox" className={styles.checkbox} readOnly />
                </th>
                {columns.filter(col => col.visible).map(col => (
                  <th
                    key={col.id}
                    className={
                      col.id === 'status'
                        ? `${styles.stickyCol} ${styles.stickyStatus}`
                        : col.id === 'actions'
                          ? `${styles.stickyCol} ${styles.stickyActions}`
                          : ''
                    }
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: '36px' }}>
                  <input type="checkbox" className={styles.checkbox} readOnly />
                </td>
                {columns.filter(col => col.visible).map(col => (
                  <td
                    key={col.id}
                    className={
                      col.id === 'status'
                        ? `${styles.stickyCol} ${styles.stickyStatus}`
                        : col.id === 'actions'
                          ? `${styles.stickyCol} ${styles.stickyActions}`
                          : ''
                    }
                  >
                    {col.id === 'name' && (
                      <div className={styles.documentName}>
                        <FileText size={14} />
                        <span>Sample.pdf</span>
                      </div>
                    )}
                    {col.id === 'type' && <span className={styles.documentType}>PDF</span>}
                    {col.id === 'size' && <span>2.50 MB</span>}
                    {col.id === 'pages' && <span>12</span>}
                    {col.id === 'uploadedAt' && <span>{new Date().toLocaleDateString()}</span>}
                    {col.id === 'status' && (
                      <span className={styles.statusBadge}>
                        <CheckCircle size={11} />
                        Completed
                      </span>
                    )}
                    {col.id === 'actions' && (
                      <div style={{ display: 'flex' }}>
                        <span className={styles.actionBtn}><ViewIcon size={13} /></span>
                        <span className={styles.actionBtn}><Download size={13} /></span>
                        <span className={styles.actionBtn}><Trash2 size={13} /></span>
                      </div>
                    )}
                    {!col.isSystem && <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default DocumentListingView;
